/* eslint-disable complexity */
import { Component, Element, Event, EventEmitter, h, Host, Prop } from '@stencil/core'
import { Build, JSX, Listen, State } from '@stencil/core/internal'
import {
    CartService,
    container,
    DetailProduct,
    LoyaltyMember,
    LoyaltyMemberService,
    LoyaltyProgram,
    ProductService,
    Properties,
    RequestQuery,
} from '@rdss/client'
import { scopify } from '@rdss/core'
import { selectProperties } from '~/selectors/properties'
import { store } from '~/store'
import { transform } from '~/transformers'
import { select } from '~/selectors'
import { BundleList } from 'src/components/wf-product-bundles'
import { recommendationSliderOptions } from './config/recommendation-slider'
import { WeinfreundeWishlistService } from '~/data-access'
import { Award } from '~/components/molecules/wf-awards'
import { transformLoyaltyMember } from 'src/transformers/loyalty/loyalty-member'
import { transformLoyaltyProgram } from 'src/transformers/loyalty/loyalty-program'
import { getLoyaltyTier, getPointsToEarn, getTierMultiplyingFactor } from 'src/selectors/loyalty'
import {
    getCategoryName,
    TrackingContext,
    TrackingDetails,
    TrackingDisturberType,
} from '@rdss/client/dist/catalog/types/tracking'

@Component({
    tag: 'wf-product-detail-page',
    styleUrl: 'wf-product-detail-page.scss',
    shadow: true,
})

/**
 * The weinfreunde product detail page.
 */
export class WfProductDetailPage {
    /**
     * Host element of the component.
     */
    @Element() host: HTMLElement

    /**
     * Load wishlist data on page load.
     */
    @Prop() loadWishlistOnRequest = true

    /**
     * The article id aka product id.
     */
    @Prop({ mutable: true }) articleId: number

    /**
     * If the article is recommended.
     * Recommended = Attribute 'Ist bei Rewe erhältlich' set.
     */
    @Prop() isRecommended = false

    /**
     * The article id aka product id.
     */
    @Prop({ mutable: true }) sku: string
    @Prop() readonly productService = container.resolve(ProductService)
    @Prop() readonly cartService = container.resolve(CartService)
    @Prop() readonly wishlistService = container.resolve(
        WeinfreundeWishlistService
    )

    @Prop() readonly loyaltyMemberService =
        container.resolve(LoyaltyMemberService)

    /**
     * SKUs of Recommendations as stringified json.
     */
    @Prop() strRecos: string

    /**
     * Translated recommendations as DetailProducts.
     */
    @Prop({ mutable: true }) recommendations: DetailProduct[] = []

    /**
     * Max number of items to select as quantity in quantity selector.
     *
     * @widgetFieldLabel Maximale Anzahl
     */
    @Prop({ mutable: true }) maxQuantity? = 150

    @Prop() enableLoyalty = false

    @Prop() paybackServiceStatus: string

    /**
     * Main category id of the product.
     */
    @Prop() category = ''

    @State() paybackActive = true

    /**
     * The product state.
     */
    @State() private product: DetailProduct

    /**
     * Initial tab index, which is shown after page load, if a hash is set.
     */
    @State() private initialTab?: number

    /**
     * Loyalty member information.
     */
    @State() private loyaltyMember?: LoyaltyMember

    /**
     * Loyalty program information.
     */
    @State() private loyaltyProgram?: LoyaltyProgram

    /**
     * Event emitted when the add to cart button is clicked.
     */
    @Event() addToCartTracking: EventEmitter<TrackingDetails>

    /**
     * Describes whether product has nutritional information or not.
     */
    private hasNutritionalInformation = false

    /**
     * Data for the tabs - titles and related content.
     */
    private tabsData = []

    private productTeaser: HTMLElement

    /**
     * Further Bundle information.
     */
    private bundleInformationNotice? =
        '+ + Hinweis: Informationen zur Lebensmittelkennzeichnung (Angaben zu ' +
        'Allergenen, Alkoholgehalt, verantwortlicher Lebensmittelunternehmer, etc.) findest du durch einen Klick ' +
        'auf die unterhalb aufgeführten Einzelflaschen. + +'

    /**
     * Handles the wishlist click.
     */
    @Listen('addToWishlistClicked')
    async handleProductToWishlist() {
        const { id, ordernumber, title } = this.product
        const wishlistItem = store.state.wishlist.find(item => item.ordernumber === ordernumber)

        if (wishlistItem) {
            await this.wishlistService.removeNote(wishlistItem.id)
        } else {
            await this.wishlistService.addNote({ id, ordernumber, name: title })
        }
    }

    /**
     * Handles the wishlist click.
     */
    @Listen('addToCartTrackingEnrich')
    async handleAddToCartTrackingEnrich(
        event?: CustomEvent<{
            quantity?: number,
            orderNumber?: string,
            context?: string,
            action?: string
        }>)
    {
        event.stopPropagation()
        event.stopImmediatePropagation()

        const {
            title,
            price,
        } = this.product

        document.dispatchEvent(new CustomEvent('addToCartTrackingEnriched', {
            detail: {
                context: event.detail.context ?? TrackingContext.productDetail,
                products: {
                    [event.detail.orderNumber]: {
                        actionType: event.detail.action ?? 'not_set',
                        category: getCategoryName(this.category),
                        disturberType: price.hasPseudoPrice ? TrackingDisturberType.discount : TrackingDisturberType.none,
                        id: event.detail.orderNumber,
                        name: title,
                        price: price.gross.toString(),
                        totalProductValue: (
                            price.gross * (event.detail.action === 'not_set' ? event.detail.quantity : event.detail.quantity - 1)
                        ).toString(),
                        units: event.detail.quantity.toString(),
                    },
                },
            } }
        ));
    }

    /**
     * Stencils lifecycle hook. Triggers when the component will be
     * load, and before the component is rendered.
     */
    async componentWillLoad() {
        await this.loadProduct()

        this.setMaxQuantity()

        this.initialTab = Number(location.hash.replace('#tabs-content-', '')) || null

        await this.loadRecommendations()
        if(this.enableLoyalty) await this.loadLoyalty()

        if (this.paybackServiceStatus === 'inactive') {
            this.paybackActive = false
        }
    }

    /**
     * @inheritDoc
     */
    async componentDidLoad() {
        this.scrollToTab()
    }

    /**
     * Renders the component.
     */
    render(): JSX.Element {
        if (!this.product) return

        const {
            manufacturer,
            title = '',
            properties,
            shipping,
            magazineLinks,
            loyalty: payback,
            media,
            content,
            unit,
            price,
            attributes,
            translation,
            prices,
            ordernumber,
            nutritionalInformation,
        } = this.product

        const {
            awards,
            badges,
            highlights,
            plusOneCampaign,
            characteristics,
            nutritionalInformationAndAdditions,
        } = select(
            attributes,
            properties,
            price,
            manufacturer,
            magazineLinks,
            ordernumber,
            nutritionalInformation
        )

        this.hasNutritionalInformation = nutritionalInformationAndAdditions?.length > 0

        const refinedAwards = this.checkIfRecommended(awards, properties)

        const productImage = media[0]

        const isAvailable = this.isArticleAvailable()

        this.initTabs()

        const year =
            characteristics?.find(c => c.name === 'Jahrgang')?.value ?? ''

        const isWine = !!characteristics?.find(c => c.name === 'Farbe')?.value[0]

        // eslint-disable-next-line no-unneeded-ternary
        const isAuth = !!this.loyaltyMember

        const manufacturerThumbnails = this.getManufacturerThumbnails()

        return (
            <Host>
                <vx-product-jsonld product={this.product} />
                <div
                    class={{
                        container: true,
                        'product-unavailable': !isAvailable,
                    }}
                >
                    <div class="header grid-container">
                        <vx-back-button />
                        <wf-awards awards={refinedAwards} />

                        <div class="product-image-container">
                            {!isAvailable && (
                                <div class="alert--product-unavailable">
                                    <strong>
                                        {
                                            translation?.DetailDataNotAvailableTitle
                                        }
                                    </strong>
                                    <p>{translation?.DetailDataNotAvailable}</p>
                                </div>
                            )}

                            {productImage &&
                                <vx-picture
                                    class="product-image"
                                    thumbnails={productImage.thumbnails}
                                    alt={productImage.description || `${title} ${year}`.trim()}
                                />
                            }
                        </div>

                        <wf-badges badges={badges} class="badges" />

                        <div class="product-info">
                            <div class="product-info__inner">
                                <div class="title">
                                    <h1>{`${title} ${year}`.trim()}</h1>
                                    <a
                                        class="supplier"
                                        href={manufacturer?.link ?? '#'}
                                    >
                                        {manufacturer?.name}
                                    </a>
                                </div>

                                {highlights?.length > 0 && (
                                    <ul class="details">
                                        {highlights.map(detail => (
                                            <li class="detail">
                                                {detail.options[0]?.name}
                                            </li>
                                        ))}
                                    </ul>
                                )}

                                <wf-price
                                    basePrice={
                                        prices[0]?.calculatedReferencePrice
                                    }
                                    baseUnit={unit.referenceUnit}
                                    packageSize={unit.purchaseUnit}
                                    unitLong={unit.name}
                                    unitShort={unit.unit}
                                    gross={price.gross}
                                    grossPseudo={price.grossPseudo}
                                    pseudoPercent={price.pseudoPercent}
                                    currency={price.currency}
                                    unitLabelType={
                                        this.isBundleProduct()
                                            ? 'bundle'
                                            : unit.unit
                                    }
                                    locale="de-DE"
                                    info="inkl. MwSt. zzgl."
                                    infoUrl="https://www.weinfreunde.de/versand"
                                />

                                <div class="article-actions">
                                    <vx-add-to-cart
                                        orderNumber={this.product.ordernumber}
                                        loading={store.state.cartIsLoading}
                                        maxQuantity={this.maxQuantity}
                                        disabled={!isAvailable}
                                        label={
                                            translation?.DetailBuyActionAddName
                                        }
                                        context={TrackingContext.productDetail}
                                    />

                                    {plusOneCampaign && (
                                        <vx-add-to-cart
                                            orderNumber={this.product.ordernumber}
                                            loading={store.state.cartIsLoading}
                                            disabled={
                                                !isAvailable ||
                                                this.product.stock <
                                                    plusOneCampaign
                                            }
                                            displayQuantitySelect={false}
                                            quantity={plusOneCampaign}
                                            label={`${
                                                plusOneCampaign - 1
                                            } + 1 Aktion`}
                                            context={TrackingContext.productDetail}
                                            action={`${
                                                plusOneCampaign - 1
                                            }+1`}
                                        />
                                    )}
                                </div>

                                <vx-wishlist productOnWishlist={this.isProductOnWishlist()}/>

                                {isAvailable && (
                                    <div class="shipping-info">
                                        <span class="shipping-info">
                                            {shipping?.info}
                                        </span>
                                    </div>
                                )}

                                {Object.keys(payback ?? {}).length > 0 && this.paybackActive && (
                                    <vx-payback
                                        info={payback.info}
                                        points={payback.points}
                                        registerLink={payback.registerLink}
                                        pointText={payback.pointText}
                                        tooltipHeadline={
                                            payback.tooltipHeadline
                                        }
                                        tooltipText={payback.tooltipText}
                                        tooltipCta={payback.tooltipCta}
                                    />
                                )}

                                {this.enableLoyalty && (
                                    <wf-loyalty-points-price-info
                                        skeleton={!Build.isBrowser}
                                        points={getPointsToEarn(price.gross, getTierMultiplyingFactor(
                                            this.loyaltyProgram?.loyaltyTiers,
                                            getLoyaltyTier(this.loyaltyMember)
                                        ))}
                                        isAuth={isAuth}
                                        hasCta={!isAuth}
                                        ctaLink={`/account/login/sTarget/detail/sArticle/${this.product.id}`}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                    <div class="content grid-container">
                        {!this.isBundleProduct() &&
                            content.shortDescription &&
                            <div class="product-description product-teaser">
                                <p class="teaser"
                                    ref={(el) => {
                                        this.productTeaser = el
                                    }} innerHTML={content.shortDescription}>
                                </p>
                            </div>
                        }

                        {!this.isBundleProduct() && [

                            manufacturerThumbnails &&
                            <vx-picture
                                quality={60}
                                height="300"
                                class="manufacturer-cover"
                                decoding="async"
                                alt={manufacturer?.coverMedia?.description || manufacturer?.name}
                                thumbnails={manufacturerThumbnails}/>,

                            <div class="product-description">
                                <vx-tabs
                                    class="tabs"
                                    initialTab={this.initialTab}
                                    data={this.tabsData}>

                                    {isWine && (
                                        <div slot="tabs-content-pre-0">
                                            <wf-wine-scale characteristics={characteristics}/>
                                        </div>
                                    )}

                                    <div slot="tabs-content-1">
                                        <vx-table
                                            columns={2}
                                            data={characteristics}
                                        />
                                    </div>

                                    {this.hasNutritionalInformation &&
                                        <div slot="tabs-content-3">
                                            <p class="nutritional-header">
                                                Durchschnittliche Nährwerte je
                                                100 ml
                                            </p>
                                            <vx-table
                                                columns={1}
                                                data={nutritionalInformationAndAdditions}
                                            />
                                        </div>
                                    }
                                </vx-tabs>
                            </div>,
                        ]}
                        {this.isBundleProduct() && (
                            <BundleList
                                article={this.product}
                                isBundleAvailable={this.isBundleAvailable()}
                                notice={this.bundleInformationNotice}
                            />
                        )}
                    </div>

                    {this.recommendations.length > 0 && (
                        <div class="recommendations-container">
                            <p class="recommendations-headline">
                                Das könnte dir auch schmecken
                            </p>
                            <div class="recommendations">
                                <vx-content-slider
                                    options={recommendationSliderOptions}
                                >
                                    {this.recommendations.map(product => (
                                        <vx-content-slide>
                                            <wf-product-tile
                                                imageLoadingHint={'lazy'}
                                                product={product}
                                                showNutritionalInformation={
                                                    true
                                                }
                                                context={TrackingContext.productDetailRecommendation}
                                                category={this.category}
                                            />
                                        </vx-content-slide>
                                    ))}
                                </vx-content-slider>
                            </div>
                        </div>
                    )}
                </div>
            </Host>
        )
    }

    /**
     * Retrieves the thumbnails for the manufacturer's banner images.
     */
    private getManufacturerThumbnails() {
        const { banner_image, banner_image_mobile } = this.product.manufacturer?.attributes?.core ?? {}

        if (!(banner_image_mobile && banner_image)) return

        return [
            { source: banner_image_mobile, maxWidth: 600 },
            { source: banner_image, maxWidth: 1440 },
        ]

    }

    /**
     * Scrolls to the tab container.
     */
    private scrollToTab() {
        // Scroll to the selected tab, if there is one.
        if (this.initialTab) {
            // Loading the product takes some time, so we need to wait a bit before scrolling to the selected tab.
            this.productTeaser.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            })
        }
    }

    /**
     * Check if product is a bundle of multiple products.
     */
    private isBundleProduct = (): boolean =>
        Object.keys(this.product.bundles ?? {}).length > 0

    /**
     * Checks if bundle available.
     */
    private isBundleAvailable = () =>
        this.isBundleProduct() &&
        Object.values(this.product.bundles).filter(
            detail =>
                (detail.quantity as number) > 0 &&
                detail.title?.length &&
                detail.isAvailable &&
                detail.content?.shortDescription?.length
        )?.length === Object.keys(this.product.bundles).length

    /**
     * Check if product is in wishlist.
     *
     * @returns {boolean} Whether product is on wishlist.
     */
    private isProductOnWishlist(): boolean {
        return store.state.wishlist?.filter(value => value.ordernumber === this.sku).length > 0
    }

    /**
     * Loads the product into the state.
     */
    private loadProduct = (sku = this.sku) => this.productService
        .findBySku(sku)
        .then(async ({ data }) => {
            this.product = data
        })
        .catch(error => console.error(WfProductDetailPage.name, 'Error while getting product', error))

    /**
     * Sets the max quantity for the quantity select.
     */
    private setMaxQuantity({ unit, stock } = this.product) {
        if (unit.maxPurchase) {
            this.maxQuantity = unit?.maxPurchase
        } else {
            this.maxQuantity =
                stock > 0 && stock < this.maxQuantity
                    ? stock
                    : this.maxQuantity
        }
    }

    /**
     * Loads the recommendations by using the provided SKUs into the state.
     */
    private loadRecommendations = async () => {
        try {
            this.recommendations = await Promise.all(
                JSON.parse(this.strRecos).map(
                    async (recommendation: DetailProduct) => {
                        const { data } = await this.productService.findBySku(
                            recommendation.ordernumber
                        )
                        return data
                    }
                )
            )
        } catch (e) {
            console.debug(
                WfProductDetailPage.name,
                'Error while getting recommendations',
                e
            )
        }
    }

    /**
     * Get Loyalty Member Information.
     */
    private loadLoyalty = async () => {
        const query: RequestQuery = {
            embed: [
                'loyalty-program',
            ],
        }

        return this.loyaltyMemberService
            .find(query)
            .then((data) => {
                this.loyaltyMember = transformLoyaltyMember(data as any)

                if(data._embedded &&
                    data._embedded['loyalty-program'] &&
                    Object.values(data._embedded['loyalty-program']).length > 0) {
                    this.loyaltyProgram = transformLoyaltyProgram(data._embedded['loyalty-program'][0] as any)
                }
            })
            .catch((e) => {
                console.debug(
                    WfProductDetailPage.name,
                    'Error while getting loyalty',
                    e
                )
            })
    }

    /**
     * Initialize the content for the tabs.
     */
    private initTabs() {
        const { description } = transform(
            this.product?.content?.description ?? ''
        )

        this.tabsData = [
            {
                title: 'Beschreibung',
                content: scopify(description, ['p'], 'sc-vx-tabs'),
                part: 'description',
            },
            {
                title: 'Steckbrief',
                content: 'Platzhalter',
            },
            {
                title: 'Hersteller',
                content: scopify(
                    this.product.manufacturer?.description ?? '',
                    ['h1', 'h2', 'h3', 'p'],
                    'sc-vx-tabs'
                ),
                part: 'manufacturer',
            },
        ]

        if (this.hasNutritionalInformation) {
            this.tabsData.push({
                title: 'Nährwerte & Zutaten',
                content: 'Platzhalter',
            })
        }
    }

    /**
     * Check if product should be advertised as 'Empfohlen bei Weinfreunde.de'.
     * If product has property 'Bei Rewe erhältlich' product is recommended.
     * Add image award to start of awards to display at top.
     *
     * @param {Award[]} awards Product awards.
     * @param {Properties} properties Product properties.
     * @returns {Award[]} Enriched product awards.
     */
    private checkIfRecommended(
        awards: Award[],
        properties: Properties
    ): Award[] {
        const recommendedAward = {
            value: 'wf-recommended',
            type: 'image',
        } as Award
        const isRecommended =
            selectProperties(properties, ['Bei Rewe erhältlich']).length > 0
        return isRecommended ? [recommendedAward, ...awards] : awards
    }

    /**
     * Checks if article is available.
     */
    private isArticleAvailable = () =>
        this.isBundleAvailable() ||
        (!this.isBundleProduct() && this.product.isAvailable)
}
